<script lang="ts">
  // Editable custom/standard tag pills for an activity -- the "+" control to apply a tag (standard
  // or the athlete's own custom ones, with a way to create a new one), and an "x" on each applied
  // tag to remove it. Every tag has a colour -- fixed for the standard ones, picked at creation for
  // custom ones -- and custom tags can be deleted from the "+" menu. That only takes them off the
  // list on offer: an activity's tags are plain names, so ones already applied stay. Activities
  // only: mount this beside the read-only TagWrapper row on activity-specific surfaces, never on a
  // shared Exercise renderer that also covers workouts.
  import { onDestroy } from 'svelte';
  import * as Popover from '$lib/components/ui/popover';
  import { Input } from '@/lib/components/ui/input';
  import { Button } from '@/lib/components/ui/button';
  import { cn } from '@/lib/utils';
  import { tagVariants } from './ActivityTag';
  import ActivityTagPill from './ActivityTagPill.svelte';
  import ActivityTagMenuList from './ActivityTagMenuList.svelte';
  import { addActivityTag, removeActivityTag } from '@/Util/api';
  import { notifyApiError, showWarningToast } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';
  import type { Exercise } from './ActivityFunctions';
  import {
    activityTagOptions,
    activityTagsSaved,
    applyTagChange,
    type ActivityTagChange,
    addCustomActivityTag,
    allTags,
    tagColors,
    tagStyle,
    removeCustomActivityTag,
    CUSTOM_TAG_COLORS,
    MAX_TAGS_PER_ACTIVITY,
    DEFAULT_CUSTOM_TAG_COLOR,
  } from './activityTagOptions';

  export let exercise: Exercise;
  export let size: 'default' | 'medium' | 'small' = 'medium';
  // Another athlete's activity (coach/squad dashboards): show its tags, but offer no editing --
  // the save endpoint is scoped to the viewer, and the tag options are the viewer's own list.
  export let readonly = false;

  const options = activityTagOptions();

  // The "+" only shows while the pointer is over the parent's tag row (or its menu is open), except
  // on touch devices, which have no hover -- same check as CalendarActivityCard. NOTE: the parent
  // must have class group/tags on whatever element should reveal it on hover.
  const hoverQuery = window.matchMedia('(hover: hover)');
  let isTouchDevice = !hoverQuery.matches;
  const onHoverQueryChange = (e: MediaQueryListEvent) => (isTouchDevice = !e.matches);
  hoverQuery.addEventListener('change', onHoverQueryChange);
  onDestroy(() => hoverQuery.removeEventListener('change', onHoverQueryChange));

  $: showAddButton = isTouchDevice || addOpen;

  // ActivityCardTitleDetails/ExerciseDialog are also mounted for workouts, sessions and forecast
  // training -- exerciseType is 'Activity' only for a real recorded/manual ride (see
  // asActivityExercise() in activityCardUtils.ts), which is the only thing tags apply to.
  $: isActivity = exercise?.exerciseType === 'Activity';

  $: appliedTags = exercise.custom_tags ?? [];
  // Every tag on offer, applied or not, so the list doubles as a toggle -- in whatever order the
  // standard list + the athlete's own custom list are in, newest custom tag last.
  $: offered = allTags($options);
  $: colors = tagColors($options);
  $: customNames = $options.custom.map((tag) => tag.name);

  let addOpen = false;
  let newTagName = '';
  let newTagColor = DEFAULT_CUSTOM_TAG_COLOR;
  // A colour picked with the pencil swatch rather than one of CUSTOM_TAG_COLORS, if any.
  let customColor: string | null = null;
  $: customColorSelected = customColor !== null && newTagColor === customColor;

  /** Whether `#rrggbb` is light enough to need a dark icon on top of it. */
  function isLightColor(color: string): boolean {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
    return 0.299 * r + 0.587 * g + 0.114 * b > 160;
  }
  let creating = false;
  // Tags with a save in flight. Each save changes just one tag, so other tags stay editable; only
  // that tag's own control waits, so adding and then removing it can't reach the server reversed.
  let pending: string[] = [];

  $: atLimit = appliedTags.length >= MAX_TAGS_PER_ACTIVITY;

  function showChange(change: ActivityTagChange) {
    exercise.custom_tags = applyTagChange(exercise.custom_tags ?? [], change);
    exercise = exercise;
  }

  async function saveTagChange(tag: string, added: boolean) {
    if (pending.includes(tag)) return;
    pending = [...pending, tag];
    const change = { path: exercise.path ?? '', tag, added };
    showChange(change);
    try {
      await (added ? addActivityTag(change.path, tag) : removeActivityTag(change.path, tag));
      activityTagsSaved.set(change);
    } catch (error) {
      // Undo just this tag -- any other edit made meanwhile stands.
      showChange({ ...change, added: !added });
      notifyApiError(error, { code: ErrorCode.ACTIVITY_TAG_UPDATE_FAILED, message: "Could not update that activity's tags." });
    } finally {
      pending = pending.filter((existing) => existing !== tag);
    }
  }

  const removeTag = (tag: string) => saveTagChange(tag, false);

  function addTag(tag: string) {
    if (appliedTags.includes(tag) || atLimit) return;
    saveTagChange(tag, true);
  }

  const toggleTag = (tag: string) => (appliedTags.includes(tag) ? removeTag(tag) : addTag(tag));

  async function createAndAddTag() {
    const name = newTagName.trim();
    if (!name || creating) return;
    creating = true;
    try {
      const { duplicateOf } = await addCustomActivityTag(name, newTagColor);
      if (duplicateOf) {
        // Left in the box to rename; the existing tag is in the list above to pick instead.
        showWarningToast(`A tag called "${duplicateOf}" already exists.`);
        return;
      }
      newTagName = '';
      newTagColor = DEFAULT_CUSTOM_TAG_COLOR;
      customColor = null;
      addTag(name);
    } catch (error) {
      notifyApiError(error, { code: ErrorCode.ACTIVITY_TAG_CREATE_FAILED, message: 'Could not add that tag.' });
    } finally {
      creating = false;
    }
  }

  async function deleteCustomTag(tag: string) {
    try {
      await removeCustomActivityTag(tag);
    } catch (error) {
      notifyApiError(error, { code: ErrorCode.ACTIVITY_TAG_DELETE_FAILED, message: 'Could not delete that tag.' });
      throw error;
    }
  }
</script>

{#if isActivity}
<div class="flex flex-row flex-wrap items-center gap-1">
  {#each appliedTags as tag (tag)}
    <ActivityTagPill
      {tag}
      color={colors.get(tag)}
      {size}
      onRemove={readonly ? undefined : () => removeTag(tag)}
      disabled={pending.includes(tag)}
    />
  {/each}

  {#if !readonly}

  <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={addOpen}>
    <Popover.Trigger asChild let:builder>
      <button
        use:builder.action
        {...builder}
        type="button"
        class={cn(
          tagVariants({ variant: 'button', size }),
          // Square, and the same height as a pill of this size (border included).
          'box-border justify-center p-0',
          { default: 'h-[26px] w-[26px]', medium: 'h-6 w-6', small: 'h-[22px] w-[22px]' }[size],
        )}
        title="Add a tag"
        on:click={(e) => e.stopPropagation()}
      >
        <i class="fa-solid fa-plus text-[10px]" aria-hidden="true"></i>
      </button>
    </Popover.Trigger>
    <Popover.Content class="w-64 min-w-0 p-2" on:click={(e) => e.stopPropagation()}>
      <div class="flex flex-col gap-2">
        {#if offered.length}
          <ActivityTagMenuList
            tags={offered}
            selected={appliedTags}
            onToggle={toggleTag}
            disabledTags={pending}
            disabledToAdd={atLimit}
            deletable={customNames}
            onDelete={deleteCustomTag}
          />
        {/if}
        <div class="flex flex-col gap-1.5 border-0 border-t border-solid border-border pt-2">
          <div class="flex flex-row gap-1">
            <Input
              class="h-8 text-sm"
              placeholder="New tag..."
              maxlength={40}
              bind:value={newTagName}
              on:keydown={(e) => { if (e.key === 'Enter') createAndAddTag(); }}
            />
            <Button size="sm" class="h-8 px-2" disabled={!newTagName.trim() || creating || atLimit} on:click={createAndAddTag}>
              Add
            </Button>
          </div>
          <div class="flex flex-row flex-wrap gap-1" role="radiogroup" aria-label="New tag colour">
            {#each CUSTOM_TAG_COLORS as swatch (swatch)}
              <button
                type="button"
                role="radio"
                aria-checked={newTagColor === swatch}
                title="Tag colour"
                class={cn(
                  'h-5 w-5 rounded-full border-2 border-solid',
                  newTagColor === swatch ? 'border-foreground' : 'border-transparent'
                )}
                style={`background-color: ${swatch}`}
                on:click={() => (newTagColor = swatch)}
              ></button>
            {/each}
            <!-- Any other colour, from the browser's picker. Dashed until one is picked; then filled
                 with it (and selected), still with the pencil to pick again. -->
            <label
              role="radio"
              aria-checked={customColorSelected}
              title="Custom colour"
              class={cn(
                'flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-2',
                customColor ? 'border-solid' : 'border-dashed border-muted-foreground',
                customColor && (customColorSelected ? 'border-foreground' : 'border-transparent')
              )}
              style={customColor ? `background-color: ${customColor}` : ''}
              on:click={() => { if (customColor) newTagColor = customColor; }}
            >
              <input
                type="color"
                class="sr-only"
                value={customColor ?? newTagColor}
                on:input={(e) => { customColor = e.currentTarget.value; newTagColor = customColor; }}
              />
              <i
                class={cn('fa-solid fa-pen text-[8px]', !customColor ? 'text-muted-foreground' : isLightColor(customColor) ? 'text-black/70' : 'text-white')}
                aria-hidden="true"
              ></i>
            </label>
          </div>
          {#if atLimit}
            <div class="text-xs text-muted-foreground">An activity can have up to {MAX_TAGS_PER_ACTIVITY} tags.</div>
          {/if}
          {#if newTagName.trim()}
            <div class="text-xs text-muted-foreground">
              Preview: <span class={cn(tagVariants({ variant: 'colored', size: 'small' }))} style={tagStyle(newTagColor)}>{newTagName.trim()}</span>
            </div>
          {/if}
        </div>
      </div>
    </Popover.Content>
  </Popover.Root>
  {/if}
</div>
{/if}
